import React from 'react';
import { VaultKeTheme } from '../../utils/theme';

const Help: React.FC = () => {
  const styles: { [key: string]: React.CSSProperties } = {
    page: {
      minHeight: '100vh',
      backgroundColor: VaultKeTheme.colors.background,
      color: VaultKeTheme.colors.text,
    },
    main: {
      paddingTop: '80px',
      paddingBottom: VaultKeTheme.spacing.xxl,
    },
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: `0 ${VaultKeTheme.spacing.xl}`,
    },
    header: {
      textAlign: 'center',
      marginBottom: VaultKeTheme.spacing.xxl,
    },
    title: {
      ...VaultKeTheme.typography.h1,
      color: VaultKeTheme.colors.text,
      marginBottom: VaultKeTheme.spacing.lg,
    },
    subtitle: {
      ...VaultKeTheme.typography.body1,
      color: VaultKeTheme.colors.textSecondary,
      maxWidth: '600px',
      margin: '0 auto',
    },
    search: {
      maxWidth: '600px',
      margin: '0 auto',
      marginBottom: VaultKeTheme.spacing.xxl,
    },
    searchInput: {
      ...VaultKeTheme.components.input,
      width: '100%',
      padding: '16px',
      fontSize: '1.1rem',
    },
    categories: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: VaultKeTheme.spacing.xl,
      marginBottom: VaultKeTheme.spacing.xxl,
    },
    category: {
      ...VaultKeTheme.components.card,
      cursor: 'pointer',
      transition: 'transform 0.3s ease',
    },
    categoryTitle: {
      ...VaultKeTheme.typography.h4,
      color: VaultKeTheme.colors.text,
      marginBottom: VaultKeTheme.spacing.md,
    },
    categoryDesc: {
      ...VaultKeTheme.typography.body2,
      color: VaultKeTheme.colors.textSecondary,
    },
    faqs: {
      marginBottom: VaultKeTheme.spacing.xxl,
    },
    faqTitle: {
      ...VaultKeTheme.typography.h2,
      color: VaultKeTheme.colors.text,
      textAlign: 'center',
      marginBottom: VaultKeTheme.spacing.xl,
    },
    faqItem: {
      ...VaultKeTheme.components.card,
      marginBottom: VaultKeTheme.spacing.md,
    },
    faqQuestion: {
      ...VaultKeTheme.typography.h5,
      color: VaultKeTheme.colors.text,
      marginBottom: VaultKeTheme.spacing.sm,
    },
    faqAnswer: {
      ...VaultKeTheme.typography.body2,
      color: VaultKeTheme.colors.textSecondary,
      lineHeight: '1.6',
    },
    contact: {
      textAlign: 'center',
      ...VaultKeTheme.components.card,
    },
    contactTitle: {
      ...VaultKeTheme.typography.h3,
      color: VaultKeTheme.colors.text,
      marginBottom: VaultKeTheme.spacing.lg,
    },
    contactText: {
      ...VaultKeTheme.typography.body1,
      color: VaultKeTheme.colors.textSecondary,
      marginBottom: VaultKeTheme.spacing.lg,
    },
    contactButton: {
      ...VaultKeTheme.components.button.primary,
    },
  };

  const categories = [
    {
      title: 'Getting Started',
      description: 'Learn the basics of setting up and using VaultKe for your chama.',
    },
    {
      title: 'Account Management',
      description: 'Manage your profile, security settings, and account preferences.',
    },
    {
      title: 'Financial Tools',
      description: 'Understand contributions, loans, dividends, and financial reporting.',
    },
    {
      title: 'Mobile App',
      description: 'Get help with the VaultKe mobile application features.',
    },
    {
      title: 'Troubleshooting',
      description: 'Common issues and how to resolve them quickly.',
    },
    {
      title: 'Security & Privacy',
      description: 'Learn about our security measures and data protection.',
    },
  ];

  const faqs = [
    {
      question: 'How do I create a new chama?',
      answer: 'To create a new chama, log into your VaultKe account and click on "Create Chama" from the dashboard. Fill in the required details including chama name, description, and member limits.',
    },
    {
      question: 'How secure is my financial data?',
      answer: 'VaultKe uses bank-grade encryption and security protocols. All data is encrypted in transit and at rest, and we follow industry best practices for financial data protection.',
    },
    {
      question: 'Can I export my financial reports?',
      answer: 'Yes, you can export financial reports in PDF and Excel formats from the Reports section of your dashboard. This includes contribution history, loan records, and dividend distributions.',
    },
    {
      question: 'What happens if I forget my password?',
      answer: 'Click on "Forgot Password" on the login page. Enter your email address and we\'ll send you a secure link to reset your password.',
    },
  ];

  return (
    <div style={styles.page}>
      <main style={styles.main}>
        <div style={styles.container}>
          <div style={styles.header}>
            <h1 style={styles.title}>Help Center</h1>
            <p style={styles.subtitle}>
              Find answers to common questions and get the help you need to make the most of VaultKe.
            </p>
          </div>

          <div style={styles.search}>
            <input
              style={styles.searchInput}
              type="text"
              placeholder="Search for help..."
            />
          </div>

          <div style={styles.categories}>
            {categories.map((category, index) => (
              <div key={index} style={styles.category}>
                <h3 style={styles.categoryTitle}>{category.title}</h3>
                <p style={styles.categoryDesc}>{category.description}</p>
              </div>
            ))}
          </div>

          <div style={styles.faqs}>
            <h2 style={styles.faqTitle}>Frequently Asked Questions</h2>
            {faqs.map((faq, index) => (
              <div key={index} style={styles.faqItem}>
                <h4 style={styles.faqQuestion}>{faq.question}</h4>
                <p style={styles.faqAnswer}>{faq.answer}</p>
              </div>
            ))}
          </div>

          <div style={styles.contact}>
            <h3 style={styles.contactTitle}>Still Need Help?</h3>
            <p style={styles.contactText}>
              Can't find what you're looking for? Our support team is here to help.
            </p>
            <button style={styles.contactButton}>Contact Support</button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Help;